/**
 * Balance presentation rules (`LLM_CONTEXT/11-balance.md` §3) — pure functions,
 * no React and no API, so the sign convention is testable in isolation.
 *
 * The web reads the balance for the report period only: `GET /balance?from&to`
 * filters **both** sides of `funded − spent` on `occurredAt`, so money outside
 * the window is invisible here even though it changes the all-time figure. That
 * windowing is not hidden from the user — the card shows the balance carried
 * into the window (the API's pre-window running balance), which is what makes a
 * period figure comparable with the all-time one.
 */

import type { BalanceMonthRow } from '../api/types';

export interface BalanceMood {
  /** Class modifier the big number is coloured from (`theme.css`). */
  modifier: 'neg' | 'pos' | 'zero';
}

/**
 * `BalanceEntry.rubAmount` carries kopecks while `PaymentEvent.rubTotal` is
 * whole rubles, so the residual can sit a fraction of a kopeck off zero. Below
 * half a kopeck the ledger is settled, not "owes 0,00 ₽".
 */
const SETTLED_TOLERANCE = 0.005;

/**
 * Sign → colour class. The only place the convention lives.
 *
 * It used to also mint a chip ("Company owes you" / "Available" / "Settled");
 * the owner asked for those to go (2026-09-08), so the signed number and its
 * colour carry the state now — red is a debt, green is money still available,
 * the default colour is even.
 */
export function balanceMood(balance: number): BalanceMood {
  if (!Number.isFinite(balance) || Math.abs(balance) < SETTLED_TOLERANCE) {
    return { modifier: 'zero' };
  }
  // negative = the company owes the owner (it funded less than was spent).
  return balance < 0 ? { modifier: 'neg' } : { modifier: 'pos' };
}

/**
 * The balance carried INTO the period: `monthlyBreakdown` opens its running
 * total on the `lt: from` aggregate, so `runningBalance − net` of the first
 * month is exactly the pre-window balance. `null` when the read asked for no
 * breakdown or the window holds no activity at all.
 */
export function carriedInBalance(perMonth: BalanceMonthRow[] | undefined): number | null {
  const first = perMonth?.at(0);
  if (!first) return null;
  return Math.round((first.runningBalance - first.net) * 100) / 100;
}
